import { RoutePointMapper } from "@/adapters/RoutePoint.mapper";
import { Base2RoutePointCreateData } from "@/adapters/Base2RoutePointTransferData.mapper";
import { OrderToRoutePointCreateData } from "@/adapters/OrderToRoutePointTransferData.mapper";
import { DataTableStore } from "@/stores/DataTableStore";
import { TableSelectStore } from "@/components/TableSelect/TableSelectStore";
import { message } from "antd";
import dayjs from "dayjs";
import { makeAutoObservable, reaction } from "mobx";
import { debounce } from "@/utils/debounce";
import { orderService } from "@/services/OrderService";
import { v4 as uuidv4 } from "uuid";
import { formatDateAndTime } from "@/utils/dateAndTimeFormat";
import { SearchFilter } from "../filters";
/**
 * RouteBuilderStore - упрощенное управление построением маршрута
 */
export class RouteBuilderStore {
    constructor(baseService, routeService, routePointsService, routeCalculationService) {
        this.baseService = baseService;
        this.routeService = routeService;
        this.routePointsService = routePointsService;
        this.routeCalculationService = routeCalculationService;
        // Выбранные элементы (порядок важен!)
        this.selectedItems = [];
        // Заказы, уже включённые в маршрут (для отображения информации)
        this.routeOrders = [];
        // Состояние
        this.isCalculating = false;
        this.isLoading = false;
        this.isInitialized = false;
        this.error = null;
        // Время начала маршрута
        this.startTime = null;
        // Кэшированная геометрия сегментов маршрута — ключ: индекс сегмента, значение: [[lat, lon], ...]
        // Заполняется методом setSegmentGeometries() из precalculatedPoints (TECH-004)
        this.segmentGeometries = new Map();
        // Приватные свойства для авто-расчёта
        this.calculationVersion = 0;
        this.autoCalculationDisposer = null;
        this.debouncedCalculate = null;
        // Мапперы для преобразования данных
        this.baseMapper = new Base2RoutePointCreateData();
        this.orderMapper = new OrderToRoutePointCreateData();
        /**
         * Обработчик выбора баз через TableSelect
         * Добавляет выбранные базы к существующему списку элементов
         * Принцип работы :
         * - если не было выбрано ни одной базы, то при первом добалении база добавится как начальная
         *  и конечноая сразу
         * - если выбрана одна база, то вторая добавится в конец списка
         * - базы всегда добавляются таким образом, что заказы располагаются между ними
         */
        this.addBase = (value) => {
            const safeValues = value || [];
            const safeValue = Array.isArray(safeValues) ? safeValues[0] : safeValues;
            if (!safeValue) {
                message.error("Выберите хотя бы одну базу");
                return;
            }
            // базы, которые уже выбраны в маршрут
            const routeBases = this.selectedItems.filter((p) => p.idBase);
            const routeOrders = this.selectedItems.filter((p) => p.idOrder);
            const basesMap = new Map(this.basesSelectStore.dataTableStore.data.map((b) => [b.id, b]));
            const baseItem = basesMap.get(safeValue);
            if (!baseItem) {
                message.error("База не найдена");
                return;
            }
            if (routeBases.length === 2) {
                message.error("Можно выбрать только 2 базы!");
                return;
            }
            const rpBase = {
                ...this.baseMapper.adapt(baseItem),
                key: uuidv4(),
            };
            let newSelectedItems = [];
            if (routeBases.length === 0) {
                // Нет баз - добавляем одну базу как начальную и конечную
                const rpBaseEnd = {
                    ...rpBase,
                    key: uuidv4(),
                };
                newSelectedItems = [rpBase, ...routeOrders, rpBaseEnd];
            }
            else {
                routeBases.push(rpBase);
                routeBases.splice(1, 0, ...routeOrders);
                newSelectedItems = routeBases;
            }
            newSelectedItems = newSelectedItems.map((p, i) => ({
                ...p,
                orderIndex: i,
            }));
            this.setSelectedItems(newSelectedItems);
        };
        /**
         * Обработчик выбора заказов через TableSelect
         * Добавляет выбранные заказы к существующему списку элементов
         * Заказы всегда добавляются между баз
         */
        this.addOrders = (value) => {
            const safeValues = value || [];
            // базы, которые уже выбраны в маршрут
            const [rpb1, rpb2] = this.selectedItems.filter((p) => p.idBase);
            if (!rpb1) {
                message.error("Добавьте хотя бы одну базу");
                return;
            }
            const routeOrders = this.selectedItems.filter((p) => p.idOrder);
            /**
             * TODO тут надо исправить dataTableStore.data,сейчас они грузятся разом +
             * после загрузки dataTableStore.data надо добавлять это в кэш
             *  */
            const ordersMap = new Map(this.ordersSelectStore.dataTableStore.data.map((b) => [b.id, b]));
            const addedOrderDoms = [];
            const addedOrders = safeValues
                .map((o) => {
                const orderItem = ordersMap.get(o);
                if (!orderItem) {
                    message.error("Заказ не найден");
                    return null;
                }
                addedOrderDoms.push(orderItem);
                const rpOrder = this.orderMapper.adapt(orderItem);
                return { ...rpOrder, key: uuidv4() };
            })
                .filter(Boolean);
            this.setRouteOrders([...this.routeOrders, ...addedOrderDoms]);
            let newSelectedItems = [
                rpb1,
                ...routeOrders,
                ...addedOrders,
            ];
            if (rpb2) {
                newSelectedItems.push(rpb2);
            }
            newSelectedItems = newSelectedItems.map((p, i) => ({
                ...p,
                orderIndex: i,
            }));
            this.setSelectedItems(newSelectedItems);
            this.ordersSelectStore.dataTableStore.clearSelection();
        };
        /**
         * Функция для отображения метки выбранной базы
         */
        this.drawBaseLabel = (base) => `${base.name}`;
        /**
         * Функция для отображения метки выбранного заказа
         */
        this.drawOrderLabel = (order) => `${order.orderNumber}`;
        // Создаем DataTableStore для баз и заказов
        this.basesTableStore = new DataTableStore({
            dataLoader: {
                loadData: async (params) => {
                    const result = await this.baseService.getBasesWithParams({
                        page: params.page,
                        pageSize: params.pageSize,
                        sorters: params.sorters,
                        filters: params.filters,
                    });
                    return {
                        data: result.data,
                        total: result.total,
                        page: params.page,
                        pageSize: params.pageSize,
                    };
                },
            },
        });
        this.ordersTableStore = new DataTableStore({
            dataLoader: {
                loadData: async () => {
                    // исключаем заказы, которые уже есть в маршруте
                    const selectedOrders = this.selectedItems.filter((item) => item.idOrder);
                    const selectedOrdersMap = new Map(selectedOrders.map((item) => [item.idOrder, item.idOrder]));
                    const result = await this.routeService.getAvailableOrders();
                    const filteredResult = result.filter((item) => !selectedOrdersMap.has(item.id));
                    return {
                        data: filteredResult,
                        total: filteredResult.length,
                        page: 1,
                        pageSize: filteredResult.length,
                    };
                },
            },
        });
        // Создаем TableSelectStore для баз и заказов
        this.basesSelectStore = new TableSelectStore(this.basesTableStore, {}, [
            {
                id: "base-search-filter",
                Cmp: SearchFilter,
                props: {
                    name: "search",
                    label: "Поиск",
                    placeholder: "Поиск по названию, адресу...",
                    allowClear: true,
                    showSearchIcon: true,
                    onChange: (value) => this.basesSelectStore?.filterStore?.updateFilter("search", value),
                },
                visible: true,
                order: 1,
            },
        ]);
        this.ordersSelectStore = new TableSelectStore(this.ordersTableStore, { multiple: true });
        makeAutoObservable(this);
    }
    // === ACTIONS ===
    /**
     * Инициализация - загружаем данные самостоятельно
     */
    async init(routeId) {
        this.reset();
        this.setLoading(true);
        try {
            // Инициализируем TableSelect stores
            await Promise.all([
                this.basesSelectStore.init(),
                this.ordersSelectStore.init(),
            ]);
            const [routePoints, routeOrders] = await Promise.all([
                this.routePointsService.get(routeId),
                orderService.getOrdersInRoute(routeId),
            ]);
            // Сохраняем заказы маршрута для отображения информации
            this.setRouteOrders(routeOrders);
            // Преобразуем загруженные точки маршрута в RoutePointDomCreateWithKey
            const routePointsWithKeys = routePoints.map((routePoint) => {
                const { id, ...routePointWithoutId } = routePoint;
                return {
                    ...routePointWithoutId,
                    key: uuidv4(), // Генерируем уникальный ключ для каждой загруженной точки
                };
            });
            this.setSelectedItems(routePointsWithKeys);
            this.setInitialized(true);
            this.setupAutoCalculation();
        }
        catch (error) {
            this.error =
                error instanceof Error ? error.message : "Ошибка загрузки данных";
            message.error(this.error);
        }
        finally {
            this.setLoading(false);
        }
    }
    /**
     * Инициализация из быстрого заказа
     */
    async initFromQuickOrder(base, order, startTime) {
        this.reset();
        this.setStartTime(startTime);
        const rpBaseStart = {
            ...this.baseMapper.adapt(base),
            key: uuidv4(),
            orderIndex: 0,
        };
        const rpOrder = {
            ...this.orderMapper.adapt(order),
            key: uuidv4(),
            orderIndex: 1,
        };
        const rpBaseEnd = {
            ...this.baseMapper.adapt(base),
            key: uuidv4(),
            orderIndex: 2,
        };
        this.setRouteOrders([order]);
        this.setSelectedItems([rpBaseStart, rpOrder, rpBaseEnd]);
        this.setInitialized(true);
        // Сразу запускаем расчет, так как данные есть
        await this.calculateRoutePointTimes(startTime);
        // Настраиваем авто-расчёт для последующих изменений
        this.setupAutoCalculation();
    }
    /**
     * Инициализация пустого маршрута (при создании напрямую, без быстрого заказа)
     */
    async initEmpty() {
        this.reset();
        this.setLoading(true);
        try {
            await Promise.all([
                this.basesSelectStore.init(),
                this.ordersSelectStore.init(),
            ]);
            this.setInitialized(true);
            this.setupAutoCalculation();
        }
        catch (error) {
            this.error =
                error instanceof Error ? error.message : "Ошибка загрузки данных";
            message.error(this.error);
        }
        finally {
            this.setLoading(false);
        }
    }
    /**
     * Инициализация из быстрого заказа по ID (для navigation state)
     */
    async initFromQuickOrderByIds(baseId, orderId, startTime) {
        this.setLoading(true);
        try {
            // Загружаем данные по ID
            const [bases, order] = await Promise.all([
                this.baseService.getBasesByIds([baseId]),
                orderService.getOrder(orderId),
            ]);
            const base = bases[0];
            if (!base) {
                throw new Error("База не найдена");
            }
            // Используем существующий метод
            await this.initFromQuickOrder(base, order, startTime);
        }
        catch (error) {
            this.error =
                error instanceof Error ? error.message : "Ошибка загрузки данных";
            message.error(this.error);
        }
        finally {
            this.setLoading(false);
        }
    }
    /**
     * Инициализация из предрассчитанных данных (без вызова route-calculation API).
     * Используется при навигации с дашборда, когда findOptimalBase уже вернул
     * precalculatedPoints с таймингами и геометрией сегментов.
     */
    async initFromPrecalculated(baseId, orderId, startTime, precalculatedPoints) {
        this.setLoading(true);
        try {
            // Загружаем базу и заказ для отображения в UI
            const [bases, order] = await Promise.all([
                this.baseService.getBasesByIds([baseId]),
                orderService.getOrder(orderId),
            ]);
            const base = bases[0];
            if (!base) {
                throw new Error("База не найдена");
            }
            this.reset();
            this.setStartTime(startTime);
            // Инициализируем маршрутные точки из предрассчитанных данных (уже с таймингами)
            const itemsFromPrecalculated = precalculatedPoints.map((dto, index) => {
                const domCreate = RoutePointMapper.toCreateDom(dto);
                return {
                    ...domCreate,
                    key: uuidv4(),
                    orderIndex: index,
                };
            });
            // Сохраняем геометрию сегментов для MapService
            this.setSegmentGeometries(precalculatedPoints);
            this.setRouteOrders([order]);
            this.setSelectedItems(itemsFromPrecalculated);
            this.setInitialized(true);
            // НЕ вызываем calculateRoutePointTimes() — данные уже рассчитаны
            // Настраиваем авто-расчёт для последующих изменений пользователем
            this.setupAutoCalculation();
        }
        catch (error) {
            this.error =
                error instanceof Error ? error.message : "Ошибка загрузки данных";
            message.error(this.error);
            // Fallback: если что-то пошло не так — инициализируем стандартно
        }
        finally {
            this.setLoading(false);
        }
    }
    /**
     * Сохранить геометрию сегментов из предрассчитанных точек.
     * Ключ — строковый индекс сегмента (от этой точки до следующей).
     */
    setSegmentGeometries(points) {
        this.segmentGeometries = new Map();
        points.forEach((point, i) => {
            if (point.segmentGeometry && point.segmentGeometry.length > 0) {
                this.segmentGeometries.set(`${i}`, point.segmentGeometry);
            }
        });
    }
    /**
     * Получить всю геометрию сегментов в виде единого массива [lat, lon] пар.
     * Используется MapService для отрисовки маршрута без дополнительных API-вызовов.
     */
    getAllSegmentGeometry() {
        const all = [];
        this.segmentGeometries.forEach((geo) => all.push(...geo));
        return all;
    }
    /**
     * Удалить элемент из маршрута по уникальному ключу
     */
    removeItem(key) {
        this.setSelectedItems(this.selectedItems.filter((item) => item.key !== key));
    }
    /**
     * Изменить порядок элементов (для DND сортировки)
     */
    reorderItems(newItems) {
        this.setSelectedItems(newItems.map((item, index) => ({
            ...item,
            orderIndex: index,
        })));
    }
    /**
     * Рассчитать времена для маршрута
     */
    async calculateRoutePointTimes(startTime) {
        if (!this.isValidRoute)
            return;
        try {
            this.setCalculating(true);
            this.clearError();
            // Только активные (не soft-deleted) точки участвуют в расчёте
            const activeItems = this.activeItems;
            const routePointDtos = activeItems.map((point) => RoutePointMapper.toCreateDto(point));
            const calculatedDtos = await this.routeCalculationService.calculateRoutePointTimes(startTime.toISOString(), routePointDtos);
            // Мёрджим рассчитанные активные точки, сохраняя soft-deleted нетронутыми
            const calculatedActive = calculatedDtos.map((dto, index) => {
                const withoutId = RoutePointMapper.toCreateDom(dto);
                return {
                    ...withoutId,
                    key: activeItems[index].key,
                };
            });
            const mergedItems = this.selectedItems.map((item) => {
                if (item.isDeleted)
                    return item;
                const updated = calculatedActive.find((c) => c.key === item.key);
                return updated ?? item;
            });
            this.setSelectedItems(mergedItems);
        }
        catch (error) {
            this.error =
                error instanceof Error ? error.message : "Ошибка расчета времен";
            message.error(this.error);
        }
        finally {
            this.setCalculating(false);
        }
    }
    // === GETTERS ===
    /**
     * Проверка валидности маршрута
     */
    get isValidRoute() {
        return !this.validationMessage;
    }
    /**
     * Сообщение валидации
     */
    get validationMessage() {
        return (this.validateBasicStructure ||
            this.validateOrdersSorting ||
            this.validateStartTime);
    }
    /**
     * Только активные (не отменённые) точки маршрута.
     * Точка считается отменённой если:
     * 1. isDeleted=true (soft-delete через триггер), ИЛИ
     * 2. orderStatus = CANCELLED / DISPATCHER_CANCELLED (заказ отменён, даже если isDeleted не обновлён)
     */
    get activeItems() {
        return this.selectedItems.filter((item) => {
            if (item.isDeleted === true)
                return false;
            const status = item.orderStatus;
            if (status === "CANCELLED" || status === "DISPATCHER_CANCELLED")
                return false;
            return true;
        });
    }
    get validateBasicStructure() {
        // Учитываем только активные (не удалённые) точки
        const activeItems = this.activeItems;
        const { length } = activeItems;
        if (length === 0)
            return "Добавьте элементы в маршрут";
        const [first, ...rest] = activeItems;
        const last = rest[rest.length - 1] || first;
        if (!first?.idBase)
            return "Первый элемент должен быть базой";
        if (!last?.idBase)
            return "Последний элемент должен быть базой";
        const bases = activeItems.filter((item) => item.idBase);
        const orders = activeItems.filter((item) => item.idOrder);
        if (bases.length !== 2)
            return "В маршруте должно быть ровно 2 базы";
        if (orders.length === 0)
            return "Между базами должен быть хотя бы один заказ";
        return null;
    }
    get validateOrdersSorting() {
        const ordersWithTimes = this.activeItems
            .filter((item) => item.idOrder)
            .filter((item) => item.plannedArrivalTime)
            .map((item) => dayjs(item.plannedArrivalTime));
        const isNotSorted = ordersWithTimes.some((time, i) => i > 0 && time.isBefore(ordersWithTimes[i - 1]));
        return isNotSorted
            ? "Заказы в маршруте должны быть отсортированы по времени прибытия по возрастанию"
            : null;
    }
    get validateStartTime() {
        if (!this.startTime || this.activeItems.length === 0)
            return null;
        const firstOrderTime = this.activeItems[1]?.plannedArrivalTime;
        if (!firstOrderTime)
            return null;
        const firstArrival = dayjs(firstOrderTime);
        return this.startTime.isAfter(firstArrival)
            ? `Время начала маршрута (${formatDateAndTime(this.startTime)}) не должно превышать время прибытия к первому заказу (${formatDateAndTime(firstArrival)})`
            : null;
    }
    setInitialized(initialized) {
        this.isInitialized = initialized;
    }
    setLoading(loading) {
        this.isLoading = loading;
    }
    setCalculating(calculating) {
        this.isCalculating = calculating;
    }
    setSelectedItems(selectedItems) {
        this.selectedItems = selectedItems;
    }
    setRouteOrders(orders) {
        this.routeOrders = orders;
    }
    /**
     * Установить время начала маршрута
     */
    setStartTime(startTime) {
        this.startTime = startTime;
    }
    // === UTILITY METHODS ===
    clearError() {
        this.error = null;
    }
    /**
     * Очистка состояния
     */
    reset() {
        this.disposeAutoCalculation();
        this.setSelectedItems([]);
        this.setRouteOrders([]);
        this.setStartTime(null);
        this.setCalculating(false);
        this.setLoading(false);
        this.setInitialized(false);
        this.calculationVersion = 0;
        this.segmentGeometries = new Map();
        this.clearError();
    }
    // === AUTO-CALCULATION METHODS ===
    /**
     * Создаёт fingerprint selectedItems для отслеживания изменений
     * Отслеживает только свойства, влияющие на расчёт времени
     */
    getItemsFingerprint() {
        // Учитываем только активные точки — soft-deleted не влияют на расчёт
        return this.activeItems
            .map((item, index) => `${index}:${item.idBase || ""}:${item.idOrder || ""}`)
            .join("|");
    }
    /**
     * Настройка автоматического пересчёта при изменении items или startTime
     * Вызывается после завершения инициализации
     */
    setupAutoCalculation() {
        this.disposeAutoCalculation();
        // Создаём debounced функцию расчёта (500ms задержка)
        this.debouncedCalculate = debounce(() => {
            this.triggerCalculation();
        }, 500);
        // Настраиваем MobX reaction для отслеживания изменений
        this.autoCalculationDisposer = reaction(
        // Data function - что отслеживаем
        () => ({
            itemsFingerprint: this.getItemsFingerprint(),
            startTime: this.startTime?.valueOf() ?? null,
            isInitialized: this.isInitialized,
        }), 
        // Effect function - выполняется при изменении данных
        (data, prevData) => {
            // Пропускаем если не инициализирован
            if (!data.isInitialized)
                return;
            // Пропускаем если ничего значимого не изменилось
            if (prevData &&
                data.itemsFingerprint === prevData.itemsFingerprint &&
                data.startTime === prevData.startTime) {
                return;
            }
            // Запускаем debounced расчёт
            this.debouncedCalculate?.();
        }, { fireImmediately: false });
    }
    /**
     * Очистка reaction авто-расчёта
     */
    disposeAutoCalculation() {
        if (this.autoCalculationDisposer) {
            this.autoCalculationDisposer();
            this.autoCalculationDisposer = null;
        }
        this.debouncedCalculate = null;
    }
    /**
     * Запуск расчёта с версионированием для обработки конкурентных запросов
     */
    async triggerCalculation() {
        // Проверяем предусловия
        if (!this.startTime || !this.isValidRoute || this.isCalculating) {
            return;
        }
        // Увеличиваем версию для отслеживания этого запроса
        const currentVersion = ++this.calculationVersion;
        try {
            this.setCalculating(true);
            this.clearError();
            // Только активные точки участвуют в расчёте; soft-deleted сохраняем отдельно
            const activeItems = this.activeItems;
            const routePointDtos = activeItems.map((point) => RoutePointMapper.toCreateDto(point));
            const calculatedDtos = await this.routeCalculationService.calculateRoutePointTimes(this.startTime.toISOString(), routePointDtos);
            // Применяем результаты только если это последний расчёт
            if (currentVersion !== this.calculationVersion) {
                return;
            }
            // Мёрджим рассчитанные активные точки обратно, сохраняя soft-deleted на их местах
            const calculatedActive = calculatedDtos.map((dto, index) => {
                const withoutId = RoutePointMapper.toCreateDom(dto);
                return {
                    ...withoutId,
                    key: activeItems[index].key,
                };
            });
            // Заменяем активные точки на рассчитанные, оставляя soft-deleted нетронутыми
            const mergedItems = this.selectedItems.map((item) => {
                if (item.isDeleted)
                    return item;
                const updated = calculatedActive.find((c) => c.key === item.key);
                return updated ?? item;
            });
            this.setSelectedItems(mergedItems);
        }
        catch (error) {
            // Показываем ошибку только если это последний расчёт
            if (currentVersion === this.calculationVersion) {
                this.error =
                    error instanceof Error ? error.message : "Ошибка расчета времен";
                message.error(this.error);
            }
        }
        finally {
            // Сбрасываем флаг только если это последний расчёт
            if (currentVersion === this.calculationVersion) {
                this.setCalculating(false);
            }
        }
    }
    get expectedArrivalTime() {
        if (this.selectedItems.length === 0 || !this.isValidRoute) {
            return null;
        }
        const lastItem = this.selectedItems[this.selectedItems.length - 1];
        return lastItem?.expectedArrivalTime
            ? dayjs(lastItem.expectedArrivalTime)
            : null;
    }
}
